import { useCallback, useEffect, useMemo, useState } from 'react'
import { Oval } from 'react-loader-spinner'
import { Image } from '../../../../../../shared/image'
import { ImageGallery } from '../../../../../../shared/swiper'
import { Message } from '../../../../../../types/api'
import { GalleryMedia } from '../../../../../../types/shared'
import { SvgIcon } from '../../../../../../shared/icons'
import { downloadFile } from '../../../../../../utils/custom-functions/useFilePreview'
import { useMessageSelection } from '../message-action/useMessageSelection'
import DecryptedMessage from './DecryptedMessage'
import LinkifyText from '../../../../../../shared/LinkifyText'
import { useTranslation } from 'react-i18next'
import { TAB_TO_SCREEN_MAP } from '../../../../../../data/components'
import { setScreen } from '../../../../../../redux/reducers/messenger/screenSlice'
import { useAppDispatch } from '../../../../../../redux/hooks'
import AnnouncementVideoPlayer from './AnnouncementVideoPlayer'

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'ogv', 'ogg'])

const isVideoMedia = (message: Message): boolean => {
  const mime = message.file_type ?? message.metadata?.mime_type
  if (mime) return mime.startsWith('video/')
  const url = String(message.file_url ?? '')
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_EXTENSIONS.has(ext)
}

const ImageMessage = ({ message, allMessages }: { message: Message; allMessages: Message[] }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [showModal, setShowModal] = useState(false)
  const [showDownload, setShowDownload] = useState(false)
  const { isSelectionMode } = useMessageSelection()

  const handleKey = () => {
    if (message?.metadata?.announcement_type === 'get_started') {
      const redirectTab = message.metadata.redirect_url
      if (!redirectTab) return
      const screen = TAB_TO_SCREEN_MAP[redirectTab]
      if (screen) {
        dispatch(setScreen(screen))
      }
    } else if (message?.metadata?.announcement_type === 'learn_more') {
      const url = message?.metadata?.action_link
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  }

  useEffect(() => {
    setShowDownload(false)
  }, [message.id])

  const galleryImages = useMemo(() => {
    if (!message) return []
    const images: GalleryMedia[] = []

    allMessages.forEach((msg) => {
      if (msg.message_type === 'image' && msg.file_url) {
        const imageSrc = msg.file_url
        if (msg.isDeleted) {
          return
        }
        images.push({
          src: imageSrc,
          alt: msg.file_type || 'image',
          messageId: msg.id,
          fileName: msg.file_type || 'image',
          type: 'image',
          isDeleted: msg.isDeleted,
        })
      }
    })

    return images
  }, [allMessages])

  const initialImageIndex = useMemo(() => {
    return galleryImages.findIndex((img) => img.messageId === message.id && img.isDeleted === message.isDeleted)
  }, [galleryImages, message.id])

  const handleImageClick = () => {
    if (isSelectionMode) {
      setShowModal(false)
    } else {
      setShowModal(true)
    }
  }

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
  }, [])

  const fileNameOriginal = message.metadata?.original_filename || 'file.jpg'

  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation() // Prevent triggering other click events

      if (!message?.file_url) {
        console.error('No file URL available for download')
        return
      }

      try {
        // Get the actual file name
        const fileName = message.metadata?.original_filename || fileNameOriginal || `image_${message.id}.jpg`

        await downloadFile(message.file_url, fileName)
      } catch (error) {
        console.error('Failed to download image:', error)
        // You might want to add user notification here
      }
    },
    [message.file_url, message.id, message.metadata?.original_filename, fileNameOriginal],
  )

  return (
    <>
      {!message.isAnnouncement ? (
        <div
          className="gallery-img"
          onMouseEnter={() => setShowDownload(true)}
          onMouseLeave={() => setShowDownload(false)}
        >
          <div className="image-wrapper" style={{ position: 'relative' }}>
            {/* Upload progress overlay */}
            {message.uploadStatus === 'uploading' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '8px',
                  zIndex: 10,
                }}
              >
                <Oval
                  visible={true}
                  height="50"
                  width="50"
                  ariaLabel="Loading"
                  wrapperClass="loader-wrapper"
                  color="#0066FF"
                />
              </div>
            )}

            {/* Failed upload overlay */}
            {message.uploadStatus === 'failed' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(220, 53, 69, 0.9)',
                  borderRadius: '8px',
                  zIndex: 10,
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: '600',
                }}
              >
                Upload Failed
              </div>
            )}

            <Image
              src={message.file_url}
              alt="message image"
              className="img-fluid"
              onClick={handleImageClick}
              style={{
                opacity: message.uploadStatus === 'uploading' ? 0.5 : 1,
              }}
            />
            {showDownload && message.uploadStatus !== 'uploading' && (
              <SvgIcon iconId="download" onClick={handleDownload} className="image-download-icon" />
            )}
          </div>
          {message.content && (
            <h5>
              <DecryptedMessage message={message}>
                {(decryptedContent) => <LinkifyText text={decryptedContent} />}
              </DecryptedMessage>
            </h5>
          )}
        </div>
      ) : (
        <div
          className="announce-preview-card"
          onMouseEnter={() => setShowDownload(true)}
          onMouseLeave={() => setShowDownload(false)}
        >
          <div className="announce-preview-card-inner">
            {message.file_url && (
              <div className="announce-preview-content" style={{ position: 'relative', overflow: 'hidden' }}>
                {/* Upload progress overlay for announcement */}
                {message.uploadStatus === 'uploading' && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '8px',
                      zIndex: 10,
                    }}
                  >
                    <Oval
                      visible={true}
                      height="50"
                      width="50"
                      ariaLabel="Loading"
                      wrapperClass="loader-wrapper"
                      color="#0066FF"
                      strokeWidth="4"
                    />
                  </div>
                )}
                {isVideoMedia(message) ? (
                  <AnnouncementVideoPlayer src={String(message.file_url)} className="announce-preview-image" />
                ) : (
                  <Image
                    src={message.file_url || '/card-bg.png'}
                    alt="Announcement media"
                    className="announce-preview-image"
                    onClick={handleImageClick}
                    style={{
                      opacity: message.uploadStatus === 'uploading' ? 0.5 : 1,
                    }}
                  />
                )}
                {showDownload && message.uploadStatus !== 'uploading' && !isVideoMedia(message) && (
                  <SvgIcon iconId="download" onClick={handleDownload} className="image-download-icon" />
                )}
              </div>
            )}
            {message?.metadata?.title && <div className="announce-preview-card-title">{message?.metadata?.title}</div>}
            {message.content && <div className="announce-preview-card-desc">{message.content}</div>}
            {message?.metadata?.announcement_type && message?.metadata?.announcement_type !== 'none' && (
              <>
                <hr />
                <div className="d-flex justify-content-center" onClick={handleKey}>
                  <button className="announce-preview-card-btn">{t(message?.metadata?.announcement_type)}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showModal && (
        <ImageGallery
          images={galleryImages}
          initialIndex={initialImageIndex}
          className="image-overlay-modal"
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}

export default ImageMessage
